import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import config from '../../../../app/config';
import MessageBox from '../../../../util/messageBox';
import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {socialAndPersonalDict} from '../../../../store/reducers/dicts';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {SocialAndPersonal} from '../../../../controls/agyle/ecds';

const COLUMNS = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('status', 'Social And Personal Circumstance', 2, 'SocPer_SnomedCt', 'dict', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('timestamp', 'Recorded Timestamp', 3, 'timestamp', 'date', '', 'medium', 'left', true, true, null, null, null),
    createAgyleTableColumn('action', 'Action', 7, null, 'action', 'notSort', 'medium', 'left', true, true, null, null, null),
];

const SocialAndPersonalCircumstances = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();

    // Multi Entry list
    const [list, setList] = useState([]);

    // Accordion
    const [expanded, setExpanded] = React.useState('');

    // Data
    const [dataForm, setDataForm] = useState({
        socPer: null,
        timestamp: null,
    });
    const [pickers, setPickers] = useState(null);

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [isWarningOpen, openWarning, closeWarning] = useOpenState(false, false);

    // Validation
    const [error, setError] = useState(false);
    const errorPicker = useRef(null);

    // Dictionaries
    const socialDict = useSelector(socialAndPersonalDict);

    // Init
    useEffect(() => {
        if (props.patient?.studies?.EmergencyCare?.PersonGroupPatient?.PatientCharacteristics_UrgentAndEmergencyCare_SocialAndPersonalCircumstances_SnomedCT) {
            let clone = _.cloneDeep(props.patient?.studies?.EmergencyCare?.PersonGroupPatient.PatientCharacteristics_UrgentAndEmergencyCare_SocialAndPersonalCircumstances_SnomedCT);
            let pushList = [];
            _.forEach(clone, element => {
                pushList.push({
                    ...element,
                    SocPer_SnomedCt: _.find(socialDict, {snomedCode: element.SocPer_SnomedCt}),
                    timestamp: moment.utc(element.SocPerRecordedTimestamp, config.ecdsDateTimeFormat).local(),
                });
            });
            setList([...pushList]);
        }
    }, []);

    const setDataDictionary = () => {
        let formattedList = _.cloneDeep(list);
        if (props.ecdsData) {
            for (const element of formattedList) {
                element.SocPer_SnomedCt = element.SocPer_SnomedCt?.snomedCode;
                delete element.timestamp;
            }
        }
        props.setData(formattedList);
    };

    useEffect(() => {
        setDataDictionary();
    }, [list]);

    const addToList = () => {
        if (validation()) {
            let data = {
                id: uuidv4() + '',
                SocPer_SnomedCt: dataForm.socPer,
                SocPerRecordedTimestamp: moment(pickers).utc().format(config.ecdsDateTimeFormat) || '',
                timestamp: pickers || '',
            };
            setList((oldArray) => [...oldArray, data]);
            clearData();
        }
    };

    const validation = () => {
        setError(!dataForm.socPer);
        return !error && !errorPicker.current && !!dataForm.socPer;
    };

    const clearData = () => {
        const clearDataForm = {
            socPer: null,
            timestamp: null,
        };
        setPickers(null);

        setDataForm(prevState => ({...prevState, ...clearDataForm}));
        setError(false);
    };

    const dateHandler = (event) => {
        if (event) {
            const date = moment(event).set('second', 0);
            setPickers(date);
        }
    };

    const handleAccordionChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const deleteReferral = () => {
        setList(prevState => prevState.filter(value => value.id !== deleteItem.id));
        setDeleteItem(null);
        closeWarning();
        clearData();
    };

    const handleDeleteItem = (item) => {
        setDeleteItem(item);
        openWarning();
    };

    const renderTable = () => {
        if (list) {
            return (
                <>
                    <AgyleAccordion
                        expanded={expanded === 'social'}
                        onChange={handleAccordionChange('social')}
                        label={t('Table')}
                    >
                        <AgyleTable
                            columns={COLUMNS}
                            data={list}
                            deleteItem={handleDeleteItem}
                        />
                    </AgyleAccordion>
                </>
            );
        }
    };

    if (props.accordion !== 'Social And Personal Circumstances') return null;

    return (
        <div className={classes.root}>
            {!_.isEmpty(list) && renderTable()}
            <Grid container spacing={2} style={{marginTop: 1}}>
                <Grid item xs={12}>
                    <SocialAndPersonal
                        label={t('Social And Personal Circumstance')}
                        value={dataForm.socPer}
                        onChange={(_e, val) => {
                            setDataForm(prevState => ({
                                ...prevState,
                                socPer: val,
                            }));
                        }}
                        error={error}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleDatePicker
                        label={t('Recorded Timestamp')}
                        value={pickers}
                        onChange={dateHandler}
                        type="dateTime"
                        minDate={calculateArrivalDate(props.patient)}
                        onError={event => errorPicker.current = event}
                        error={error}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleButton
                        variant="contained"
                        onClick={addToList}
                        style={{marginLeft: 'auto'}}
                    >
                        {t('Add Record')}
                    </AgyleButton>
                </Grid>
            </Grid>

            <MessageBox
                open={isWarningOpen}
                onClose={closeWarning}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={deleteReferral}
                warning
            />
        </div>
    );
};

export default SocialAndPersonalCircumstances;
